import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProgressSummary } from '../api';
import { loadRecentTopics } from '../utils/recentActivity';

// Below this, an accuracy figure is too thin to act on.
const RELIABLE_SAMPLE = 3;
const STRONG_ACCURACY = 70;

function PracticeInsightsPanel({ authEmail, authPassword, refreshKey }) {
  const navigate = useNavigate();
  const recentTopics = useMemo(() => loadRecentTopics(3), []);
  const context = recentTopics[0] || null;

  const [majorTopics, setMajorTopics] = useState([]);
  const [loading, setLoading] = useState(Boolean(authEmail && authPassword));

  useEffect(() => {
    if (!authEmail || !authPassword) {
      setMajorTopics([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getProgressSummary(authEmail, authPassword, {
      state: context?.state,
      gradeLevel: context?.grade,
    })
      .then((data) => {
        if (cancelled) return;
        const grades = data.summary || [];
        setMajorTopics(grades.flatMap((grade) => (
          (grade.majorTopics || []).map((topic) => ({
            ...topic,
            state: grade.state,
            gradeLevel: grade.gradeLevel,
          }))
        )));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setMajorTopics([]);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [authEmail, authPassword, context?.state, context?.grade, refreshKey]);

  const ranked = useMemo(() => rankTopics(majorTopics), [majorTopics]);
  const advice = useMemo(() => buildAdvice(ranked), [ranked]);

  const topicPath = (topic) => (
    `/state/${encodeURIComponent(topic.state)}/grade/${encodeURIComponent(topic.gradeLevel)}` +
    `/topic/${encodeURIComponent(topic.name)}`
  );

  return (
    <>
      {recentTopics.length > 0 && (
        <div className="insights-block">
          <div className="pane-title">Recently practiced</div>
          <div className="recent-topic-list">
            {recentTopics.map((entry) => (
              <button
                key={entry.path}
                type="button"
                className="recent-topic-row"
                onClick={() => navigate(entry.path)}
              >
                <span className="recent-topic-name">{entry.majorTopic}</span>
                <span className="recent-topic-meta">
                  {[entry.grade, entry.skill].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="insights-block">
        <div className="pane-title">
          Accuracy by topic{context?.grade ? ` · ${context.grade}` : ''}
        </div>

        {!authEmail && (
          <p className="insights-empty">Log in to see your accuracy by topic.</p>
        )}

        {authEmail && loading && (
          <p className="insights-empty">Loading your accuracy…</p>
        )}

        {authEmail && !loading && ranked.length === 0 && (
          <p className="insights-empty">No topic data yet.</p>
        )}

        {authEmail && !loading && ranked.length > 0 && (
          <div className="topic-accuracy-list">
            {ranked.map((topic) => (
              <button
                key={`${topic.gradeLevel}-${topic.name}`}
                type="button"
                className="topic-accuracy-row"
                onClick={() => navigate(topicPath(topic))}
                title={`${topic.correct} correct out of ${topic.graded} graded`}
              >
                <span className="topic-accuracy-head">
                  <span className="topic-accuracy-name">{topic.name}</span>
                  <span className="topic-accuracy-value">
                    {topic.graded ? `${topic.accuracy}%` : '—'}
                  </span>
                </span>
                <span className="progress-track topic-accuracy-track">
                  <span
                    className={`progress-fill accuracy-${accuracyTone(topic)}`}
                    style={{ width: `${topic.graded ? topic.accuracy : 0}%` }}
                  />
                </span>
                <span className="topic-accuracy-meta">
                  {topic.graded
                    ? `${topic.correct} of ${topic.graded} answered correctly`
                    : 'Not practiced yet'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {authEmail && !loading && advice && (
        <div className="insights-block">
          <div className="pane-title">What to practice next</div>
          <div className="advice-card">
            <span className="advice-headline">{advice.headline}</span>
            <span className="advice-body">{advice.body}</span>
            {advice.topic && (
              <button
                type="button"
                className="ghost advice-cta"
                onClick={() => navigate(topicPath(advice.topic))}
              >
                Go to {advice.topic.name}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Practised topics first (weakest at the top, since that's where the work is),
// then everything still untouched.
function rankTopics(topics) {
  const practised = topics
    .filter((topic) => topic.graded > 0)
    .sort((a, b) => a.accuracy - b.accuracy);
  const untouched = topics
    .filter((topic) => topic.graded === 0)
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...practised, ...untouched];
}

function accuracyTone(topic) {
  if (!topic.graded) return 'empty';
  if (topic.accuracy >= 85) return 'high';
  if (topic.accuracy >= STRONG_ACCURACY) return 'mid';
  return 'low';
}

function buildAdvice(ranked) {
  if (!ranked.length) return null;

  const weakest = ranked.filter((topic) => topic.graded >= RELIABLE_SAMPLE)[0];
  const thin = ranked.filter(
    (topic) => topic.graded > 0 && topic.graded < RELIABLE_SAMPLE
  );
  const untouched = ranked.find((topic) => topic.graded === 0);

  if (weakest && weakest.accuracy < STRONG_ACCURACY) {
    return {
      headline: `Review ${weakest.name}`,
      body: `This is your weakest topic at ${weakest.accuracy}%. A few more questions here will lift your overall accuracy fastest.`,
      topic: weakest,
    };
  }

  // A topic you're getting wrong deserves attention before a brand-new one,
  // even when there aren't enough answers for a trustworthy percentage yet.
  const strugglingThin = thin.find((topic) => topic.accuracy < STRONG_ACCURACY);
  if (strugglingThin) {
    return {
      headline: `Keep going in ${strugglingThin.name}`,
      body: strugglingThin.correct === 0
        ? `You've missed all ${strugglingThin.graded} answer${strugglingThin.graded !== 1 ? 's' : ''} here so far. Work through a few more to find the step that's tripping you up.`
        : `Only ${strugglingThin.graded} answers graded here, and ${strugglingThin.correct} landed. A few more will show what needs work.`,
      topic: strugglingThin,
    };
  }

  if (untouched) {
    return {
      headline: `Try ${untouched.name}`,
      body: weakest
        ? `You're holding ${weakest.accuracy}% or better everywhere you've practiced. This topic is still untouched.`
        : 'You haven\'t practiced this topic yet.',
      topic: untouched,
    };
  }

  if (thin.length > 0) {
    return {
      headline: `Keep going in ${thin[0].name}`,
      body: `Only ${thin[0].graded} answer${thin[0].graded !== 1 ? 's' : ''} graded so far — a few more will show whether it's really solid.`,
      topic: thin[0],
    };
  }

  if (weakest) {
    return {
      headline: `Strengthen ${weakest.name}`,
      body: `You're strong across the board. At ${weakest.accuracy}%, this topic still has the most room left.`,
      topic: weakest,
    };
  }

  return null;
}

export default PracticeInsightsPanel;
